import React, { useState } from 'react';
import { 
  CheckCircle2, 
  Sparkles, 
  ArrowRight, 
  Target, 
  Search, 
  TrendingUp, 
  ShoppingBag, 
  HelpCircle, 
  BarChart, 
  Layers, 
  DollarSign, 
  ShieldCheck, 
  Info,
  SlidersHorizontal,
  Flame
} from 'lucide-react';
import { digitalMarketingPlans, situationComparisonData } from '../../data/marketingPlansData';
import { DigitalMarketingPlan } from '../../types';

interface DigitalMarketingPlansProps {
  onSelectPlan: (planName: string) => void;
}

export const DigitalMarketingPlans: React.FC<DigitalMarketingPlansProps> = ({ onSelectPlan }) => {
  const [currency, setCurrency] = useState<'USD' | 'GBP' | 'CAD' | 'INR'>('USD');
  const [selectedSituationIndex, setSelectedSituationIndex] = useState<number>(0);

  const activePlan = digitalMarketingPlans[selectedSituationIndex];

  const formatPrice = (plan: DigitalMarketingPlan) => {
    switch (currency) {
      case 'GBP':
        return `£${Math.round(plan.monthlyUsd * 0.80)}`;
      case 'CAD':
        return `CA$${Math.round(plan.monthlyUsd * 1.36)}`;
      case 'INR':
        return `₹${plan.monthlyInr.toLocaleString('en-IN')}`;
      case 'USD':
      default:
        return `$${plan.monthlyUsd}`;
    }
  };

  return (
    <section className="bg-[#071426] py-20 border-b border-[#1b2f4a] relative" id="marketing-plans">
      {/* Background radial gradient */}
      <div className="absolute top-10 right-10 w-80 h-80 bg-[#FF851B]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#159DE8]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <Search size={14} className="text-[#159DE8]" />
            <span>KEYWORD-BASED PERFORMANCE MARKETING</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display tracking-tight leading-tight">
            Digital Marketing Keyword-Based Plans
          </h2>

          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            Transparent, minimal rate marketing packages engineered around strategic search keyword counts, Google Ads campaign management, and your exact business situation.
          </p>

          {/* Currency Toggle */}
          <div className="inline-flex items-center p-1 rounded-full bg-[#050A13] border border-[#1b2f4a] mt-2 gap-1 flex-wrap justify-center">
            {[
              { id: 'USD', label: '🇺🇸 USD ($)' },
              { id: 'GBP', label: '🇬🇧 GBP (£)' },
              { id: 'CAD', label: '🇨🇦 CAD ($)' },
              { id: 'INR', label: '🇮🇳 INR (₹)' },
            ].map((c) => (
              <button
                key={c.id}
                onClick={() => setCurrency(c.id as any)}
                className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                  currency === c.id
                    ? 'bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white shadow'
                    : 'text-[#8FA0B8] hover:text-white'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* INTERACTIVE SITUATION MATCHER: WHICH PLAN SUITS YOUR SITUATION? */}
        {/* ========================================================================= */}
        <div className="mb-16 p-6 sm:p-8 rounded-3xl bg-[#0c1a2d] border border-[#1b2f4a] shadow-card-dark">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-6 border-b border-[#1b2f4a]/70">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-mono font-bold text-[#FF851B] uppercase tracking-wider mb-1">
                <SlidersHorizontal size={14} />
                <span>Situation-Based Recommendation Guide</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white font-display">
                Which Digital Marketing Plan Suits Your Business Situation?
              </h3>
            </div>
            <span className="text-xs text-[#8FA0B8] bg-[#050A13] px-3 py-1.5 rounded-lg border border-[#1b2f4a]">
              Select your business profile below
            </span>
          </div>

          {/* 3 Situation Selector Tabs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-6">
            {[
              {
                title: 'Informative Website & Low Business / Startup',
                icon: <Info size={16} className="text-[#159DE8]" />,
                desc: 'Modest traffic, informative pages, minimal ad budget required.'
              },
              {
                title: 'Growing Service Firm & Regional Practice',
                icon: <TrendingUp size={16} className="text-[#FF851B]" />,
                desc: 'Steady baseline traffic, looking to outrank regional rivals.'
              },
              {
                title: 'E-Commerce Store & Big Market Reach',
                icon: <ShoppingBag size={16} className="text-[#00C853]" />,
                desc: 'Large product catalog, national market, Google Shopping & PMax.'
              }
            ].map((tab, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedSituationIndex(idx)}
                className={`p-4 rounded-xl text-left border transition-all duration-200 flex flex-col justify-between ${
                  selectedSituationIndex === idx
                    ? 'bg-[#0B2342] border-[#159DE8] shadow-md ring-1 ring-[#159DE8]'
                    : 'bg-[#071426] border-[#1b2f4a] hover:border-[#159DE8]/50 text-[#8FA0B8]'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded bg-[#050A13] border border-[#1b2f4a]">
                    {tab.icon}
                  </div>
                  <span className={`text-xs font-bold ${selectedSituationIndex === idx ? 'text-white' : 'text-[#D5DFEC]'}`}>
                    Scenario 0{idx + 1}
                  </span>
                </div>
                <div className={`text-xs sm:text-sm font-semibold mb-1 ${selectedSituationIndex === idx ? 'text-[#FF851B]' : 'text-white'}`}>
                  {tab.title}
                </div>
                <p className="text-[11px] text-[#8FA0B8] leading-tight">
                  {tab.desc}
                </p>
              </button>
            ))}
          </div>

          {/* Recommended Solution Card for Selected Situation */}
          <div className="p-6 rounded-2xl bg-[#050A13] border border-[#159DE8]/40 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-[#1b2f4a]">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded bg-[#00C853]/10 text-[#00C853] text-xs font-bold border border-[#00C853]/30">
                  Recommended Plan Match
                </span>
                <span className="text-white font-bold text-base sm:text-lg">
                  {activePlan.name}
                </span>
              </div>
              <div className="text-sm font-mono text-white">
                Base Rate: <span className="text-[#FF851B] font-bold text-lg">{formatPrice(activePlan)}</span> / mo
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="space-y-2 p-3.5 rounded-xl bg-[#071426] border border-[#1b2f4a]">
                <div className="text-[#8FA0B8] font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <Target size={14} className="text-[#159DE8]" />
                  <span>Why this plan matches your condition</span>
                </div>
                <p className="text-[#D5DFEC] leading-relaxed">
                  {activePlan.suitabilityScenario}
                </p>
              </div>

              <div className="space-y-2 p-3.5 rounded-xl bg-[#071426] border border-[#1b2f4a]">
                <div className="text-[#8FA0B8] font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <BarChart size={14} className="text-[#FF851B]" />
                  <span>Integrated Google Ads Strategy</span>
                </div>
                <p className="text-[#D5DFEC] leading-relaxed">
                  {activePlan.googleAdsPackage.campaignTypes.join(' • ')} with {activePlan.googleAdsPackage.recommendedAdSpend}.
                </p>
              </div>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-xs text-[#8FA0B8] flex items-center gap-2">
                <ShieldCheck size={16} className="text-[#00C853]" />
                <span>Zero long-term lock-in contracts • Transparent monthly reporting</span>
              </div>
              <button
                onClick={() => onSelectPlan(activePlan.name)}
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold hover:shadow-glow-orange transition-all flex items-center justify-center gap-1.5 shrink-0"
              >
                <span>Inquire for {activePlan.name}</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* 3 PRICING & DELIVERABLES CARDS */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {digitalMarketingPlans.map((plan) => {
            const isSelected = plan.id === activePlan.id;
            return (
              <div
                key={plan.id}
                className={`p-7 sm:p-8 rounded-3xl flex flex-col justify-between transition-all duration-300 relative group ${
                  plan.isPopular
                    ? 'bg-[#0c1a2d] border-2 border-[#FF851B] shadow-2xl scale-[1.02] z-20'
                    : 'bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8]/60 shadow-card-dark'
                }`}
              >
                {/* Popular / Special Badge */}
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-3.5 py-1 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-[11px] font-extrabold uppercase tracking-wider shadow-md flex items-center gap-1">
                      <Flame size={12} className="fill-white" />
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div>
                  {/* Tier Subtitle & Title */}
                  <div className="space-y-1 mb-4">
                    <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-[#159DE8] block">
                      {plan.tier}
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-white font-display">
                      {plan.name}
                    </h3>
                    <p className="text-xs text-[#8FA0B8] leading-relaxed pt-1">
                      {plan.description}
                    </p>
                  </div>

                  {/* Pricing Display */}
                  <div className="p-4 rounded-2xl bg-[#050A13] border border-[#1b2f4a] mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl sm:text-4xl font-extrabold text-white">
                        {formatPrice(plan)}
                      </span>
                      <span className="text-xs text-[#8FA0B8] font-medium">/ month</span>
                    </div>
                    <div className="text-[11px] text-[#00C853] font-semibold mt-1">
                      Minimal Base Retainer • No Hidden Setup Fees
                    </div>
                  </div>

                  {/* Key Highlights: Keyword Count & Condition */}
                  <div className="space-y-3 mb-6 pb-6 border-b border-[#1b2f4a]">
                    <div className="p-3 rounded-xl bg-[#071426] border border-[#1b2f4a] space-y-1">
                      <div className="text-[11px] font-bold text-[#FF851B] uppercase tracking-wider flex items-center gap-1.5">
                        <Target size={13} />
                        <span>Keyword Allocation</span>
                      </div>
                      <div className="text-xs font-bold text-white">
                        {plan.keywordsCount}
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-[#071426] border border-[#1b2f4a] space-y-1">
                      <div className="text-[11px] font-bold text-[#159DE8] uppercase tracking-wider flex items-center gap-1.5">
                        <Info size={13} />
                        <span>Suited For Condition</span>
                      </div>
                      <div className="text-xs text-[#D5DFEC]">
                        {plan.suitabilityCondition}
                      </div>
                    </div>
                  </div>

                  {/* Google Ads Inclusions */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-1.5">
                        <TrendingUp size={14} className="text-[#FF851B]" />
                        Google Ads Management:
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {plan.googleAdsPackage.campaignTypes.map((camp, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs text-[#D5DFEC]">
                          <CheckCircle2 size={13} className="text-[#00C853] shrink-0 mt-0.5" />
                          <span>{camp}</span>
                        </div>
                      ))}
                    </div>
                    <div className="p-2.5 rounded-lg bg-[#071426] border border-[#1b2f4a]/70 text-[11px] text-[#8FA0B8]">
                      <strong className="text-white">Recommended Ad Spend:</strong> {plan.googleAdsPackage.recommendedAdSpend}
                    </div>
                  </div>

                  {/* SEO & Organic Deliverables */}
                  <div className="space-y-3 mb-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-1.5">
                      <Search size={14} className="text-[#159DE8]" />
                      Organic SEO & Technical Growth:
                    </span>
                    <div className="space-y-1.5">
                      {plan.seoFeatures.slice(0, 4).map((feat, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs text-[#8FA0B8]">
                          <CheckCircle2 size={13} className="text-[#159DE8] shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Card Action Button */}
                <div className="pt-6 border-t border-[#1b2f4a]">
                  <button
                    onClick={() => onSelectPlan(plan.name)}
                    className={`w-full py-3 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                      plan.isPopular
                        ? 'bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white hover:shadow-glow-orange shadow-md'
                        : 'bg-[#071426] border border-[#1b2f4a] text-white hover:border-[#159DE8] hover:text-[#159DE8]'
                    }`}
                  >
                    <span>Get Started with {plan.name.split(' ')[0]}</span>
                    <ArrowRight size={14} />
                  </button>
                  <p className="text-[10px] text-center text-[#8FA0B8] mt-2">
                    Month-to-month flexibility • Custom keyword expansion available
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Commercial Transparency & Honest SEO Practices */}
        <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-[#050A13] border border-[#1b2f4a] space-y-3">
          <div className="flex items-center gap-2 text-xs font-mono font-bold text-[#00C853] uppercase tracking-wider">
            <ShieldCheck size={16} />
            <span>Ethical Search Practice • No Fabricated Guarantees</span>
          </div>
          <h4 className="text-base font-bold text-white font-display">
            Why EBON Media Never Promises Fake "Page 1 Rankings in 30 Days"
          </h4>
          <p className="text-xs text-[#8FA0B8] leading-relaxed">
            Legitimate search engine rankings and commercial inquiries depend on real-world factors: industry competition density across the USA, UK, and Canada, domain history, content quality, and technical health. We do not use black-hat shortcuts that jeopardize client domains. Instead, we execute proven on-page optimization, technical speed enhancements, search intent content mapping, and high-ROAS Google Ads campaigns that predictably win qualified business.
          </p>
        </div>

        {/* Feature Comparison Table Callout */}
        <div className="mt-14 p-6 sm:p-8 rounded-2xl bg-[#0c1a2d]/80 border border-[#1b2f4a] flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1">
            <h4 className="text-base sm:text-lg font-bold text-white">
              Need a Custom Hybrid Plan or Enterprise Multi-Country Strategy?
            </h4>
            <p className="text-xs sm:text-sm text-[#8FA0B8]">
              We customize keyword packages for niche US, UK, and global multi-lingual markets, offering custom Google Ads budget management and white-glove reporting.
            </p>
          </div>
          <button
            onClick={() => onSelectPlan('Custom Digital Marketing Solution')}
            className="px-6 py-3 rounded-full bg-[#050A13] border border-[#159DE8] text-[#159DE8] hover:bg-[#159DE8] hover:text-white transition-all text-xs font-bold shrink-0 flex items-center gap-2"
          >
            <span>Request Custom Scope</span>
            <ArrowRight size={14} />
          </button>
        </div>

      </div>
    </section>
  );
};
